import formatter from 'format-number';
import { startCase, toLower } from 'lodash';
import moment, { Moment } from 'moment/moment';
import { createSelector } from 'reselect';

export const formatRelativeDate = (appDateTime: string | Date | Moment) => {
  const date = moment.utc(appDateTime).toDate();
  return moment(date).fromNow();
};

export const formatDate = (date: string | Date | Moment | null | undefined, mask = 'MM/DD/YYYY') =>
  date ? moment.utc(date).format(mask) : null;
export const formatDateTime = (dateTime: string | Date | null | undefined) =>
  moment(dateTime).format('dddd, MMMM Do YYYY [at] h:mma');
export const formatDateTimeShort = (dateTime: string | Date | null | undefined) =>
  dateTime ? moment(dateTime).format('MMM Do YYYY, h:mma') : null;
export const formatUnixDate = (date: number, mask: string) =>
  date ? moment.unix(date).format(mask) : null;

export const formatYearDiff = (startDate: string, endDate: string) => {
  const dateStart = moment(startDate);
  let dateEnd = moment(endDate);
  if (endDate === '') {
    dateEnd = moment();
  }
  const duration = moment.duration(dateEnd.diff(dateStart));
  return duration.asYears();
};

export const formatMonthDiff = (startDate: string | Date, endDate: string | Date) => {
  const dateStart = moment(startDate);
  let dateEnd = moment(endDate);
  if (endDate === '') {
    dateEnd = moment();
  }
  const duration = moment.duration(dateEnd.diff(dateStart));
  return duration.asMonths();
};

export const formatPhoneNumber = createSelector(
  (phoneNumber: string | null | undefined) => phoneNumber,
  (phoneNumber: string | null | undefined) => {
    if (!phoneNumber) {
      return null;
    }
    const phoneNumber2 = phoneNumber.replace(/\D/g, '').slice(-10);
    const formattedNumber = phoneNumber2.match(/^(\d{3})(\d{3})(\d{4})$/);
    return !formattedNumber
      ? null
      : `(${formattedNumber[1]}) ${formattedNumber[2]}-${formattedNumber[3]}`;
  },
);

/**
 * puts in thousands separator commas.
 * Taken from: https://stackoverflow.com/a/2901298/26197
 */
export const formatNumberWithCommas = (toFormat: number | null | undefined): string =>
  (toFormat ?? '').toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const formatStringMaxLength = (toFormat: any, length: number): string => {
  const textString = toFormat?.toString() || '';
  return textString.length < length ? textString : `${textString.substring(0, length)}…`;
};

export const currencyFormat = (number: number | null | undefined) => {
  if (!number) return '$0';
  return formatter({
    round: 2,
    prefix: '$',
  })(number);
};

export const formatExperienceOrLicense = (stringToFormat: string, numExtra: number) => {
  if (numExtra === 0) {
    return stringToFormat;
  }
  return `${stringToFormat} and ${numExtra} more...`;
};

export const combineFullAddress = (
  officeStreet: string | null,
  officeUnit: string | null,
  officeCity: string | null,
  officeState: string | null,
  officeZipCode: string | null,
) => {
  if (!(officeStreet && officeCity && officeState && officeZipCode)) {
    return null;
  }
  return `${officeStreet}, ${
    officeUnit ? `Unit ${officeUnit}, ` : ''
  }${officeCity}, ${officeState} ${officeZipCode}`;
};

export const formatLocation = (location: {
  city: string | null | undefined;
  state: string | null | undefined;
}) => {
  if (location.city && location.state) {
    return `${location.city}, ${location.state}`;
  }
  if (location.city) {
    return location.city;
  }
  if (location.state) {
    return location.state;
  }

  return null;
};

export const formatCompensation = (compensation: {
  compensationLow: number | null;
  compensationHigh: number | null;
  compensationType: string | null;
}) => {
  let builder = null;
  if (compensation.compensationLow && !compensation.compensationHigh) {
    builder = `${currencyFormat(compensation.compensationLow)}`;
  } else if (!compensation.compensationLow && compensation.compensationHigh) {
    builder = `${currencyFormat(compensation.compensationHigh)}`;
  } else if (compensation.compensationLow && compensation.compensationHigh) {
    if (compensation.compensationLow === compensation.compensationHigh) {
      builder = `${currencyFormat(compensation.compensationLow)}`;
    } else {
      builder = `${currencyFormat(compensation.compensationLow)} - ${currencyFormat(
        compensation.compensationHigh,
      )}`;
    }
  }

  if (compensation.compensationType) {
    if (compensation.compensationType === 'ANNUAL') {
      if (builder) {
        builder = `${builder} annually`;
      } else {
        builder = 'paid annually';
      }
    } else if (builder) {
      builder = `${builder} per hour`;
    } else {
      builder = 'paid hourly';
    }
  }

  return builder;
};

export const formatPartialDays = (numDays: number) => {
  let numberOfDays = Math.round(numDays);
  const partialDays = numDays % 1;
  let hours = Math.round(partialDays * 24);

  if (hours === 24) {
    numberOfDays += 1;
    hours = 0;
  }

  let phraseBuilder = '';
  if (numberOfDays > 0) {
    phraseBuilder = `${numberOfDays} day${numberOfDays !== 1 ? 's' : ''}`;
  }
  if (hours > 0) {
    phraseBuilder = `${phraseBuilder} ${hours} hr${hours !== 1 ? 's' : ''}`;
  }
  return phraseBuilder;
};

export const formatDuration = (seconds: number) => {
  let remaining = seconds;
  const hours = Math.floor(remaining / 3600);
  remaining %= 3600;
  const minutes = Math.floor(remaining / 60);
  const remainingSeconds = remaining % 60;

  const text = [];
  if (hours > 0) {
    text.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  }
  if (minutes > 0) {
    text.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
  }
  if (remainingSeconds > 0) {
    text.push(`${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`);
  }
  return text.join(', ');
};

export const formatApproximateDuration = (minutes: number) => {
  let duration: {
    number: number;
    label: string;
  };

  if (minutes < 60) {
    duration = { number: Math.round(minutes), label: 'min' };
  } else if (minutes < 60 * 24) {
    duration = { number: Math.round(minutes / 60), label: 'hr' };
  } else if (minutes < 60 * 24 * 7) {
    duration = { number: Math.round(minutes / 60 / 24), label: 'day' };
  } else {
    duration = { number: Math.round(minutes / 60 / 24 / 7), label: 'wk' };
  }

  duration.label = `${duration.label}${duration.number === 1 ? '' : 's'}`;

  return duration;
};

export const convertPhoneNumberToE164 = (number?: string | number | null) => {
  if (!number) {
    return null;
  }

  const stringNumber = String(number);

  /*
  If this is already in E.164 format, return it
   */
  if (/^\+1[\d]{10}$/g.test(stringNumber)) {
    return stringNumber;
  }

  // Replace any non-numeric values, if number starts with a "1" and has 10 trailing numbers, return only the last 10 numbers
  const sanitized = stringNumber.replace(/[^\d]/g, '').replace(/^(1)([\d]{10})/, '$2');
  if (sanitized.length !== 10) {
    throw Error('Phone number must have exactly 10 digits');
  }
  return `+1${sanitized}`;
};

export const combineWords = (words: Array<string>, conjunction = 'and') => {
  switch (words.length) {
    case 0:
      return '';
    case 1:
      return words[0];
    case 2:
      return words.join(` ${conjunction} `);
    default:
      return [words.slice(0, -1).join(', '), words.slice(-1)[0]].join(`, ${conjunction} `);
  }
};

export const capitalize = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

export const humanize = (value: string) => startCase(toLower(value || ''));

export const humanizeCamelCaseString = (value: string) =>
  value.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

export const humanizeSnakeCaseString = (value: string) =>
  startCase(toLower((value || '').replaceAll('_', ' ')));

// IF (09/28/20): This function is to truncate long conversations. If there are less than 10
// messages return all of them. Otherwise return all messages from the past week. If there are less
// than 10 messages from the past week, show the last 10 messages.
export const truncateConversation = (messages: Array<any>, dateFunc: (m: any) => string) => {
  const INITIAL_MESSAGES_TO_SHOW = 10;

  if (messages.length <= INITIAL_MESSAGES_TO_SHOW) {
    return messages;
  }

  const latestTenMessages = messages.slice(
    messages.length - INITIAL_MESSAGES_TO_SHOW,
    messages.length,
  );
  const otherRecentMessages = messages
    .slice(0, messages.length - INITIAL_MESSAGES_TO_SHOW)
    .filter((m) => moment(dateFunc(m)).isAfter(moment().add(7, 'days')));
  return otherRecentMessages.concat(latestTenMessages);
};

export const isDateOutsideBusinessHours = (date: Moment | Date | null) => {
  if (!date) {
    return null;
  }
  const format = 'hh:mm a';
  const scheduledTime = moment(moment(date).format(format), format);
  const normalStartHour = moment('08:00:00', format);
  const normalEndHour = moment('18:00:00', format);

  return !scheduledTime.isBetween(normalStartHour, normalEndHour, undefined, '[]');
};
